import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, File, Image, FileSpreadsheet } from 'lucide-react';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const typeIcons = {
  catalog: FileText, technical_spec: FileSpreadsheet, certification: File,
  contract: FileText, invoice: FileSpreadsheet, other: File, general: File
};
const typeColors = {
  catalog: 'text-blue-400', technical_spec: 'text-primary', certification: 'text-emerald-400',
  contract: 'text-amber-400', invoice: 'text-red-400'
};

export default function Documents({ lang, tenantId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [uploadForm, setUploadForm] = useState({ name: '', doc_type: 'general', file: null });

  useEffect(() => { loadDocuments(); }, [tenantId, filterType]);

  async function loadDocuments() {
    setLoading(true);
    try {
      const params = { tenant_id: tenantId };
      if (filterType) params.doc_type = filterType;
      const { data } = await axios.get(`${API}/documents`, { params, headers: headers() });
      setDocuments(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleUpload() {
    if (!uploadForm.file || !uploadForm.name) {
      toast.error(lang === 'pl' ? 'Nazwa i plik s\u0105 wymagane' : 'Name and file required');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      await axios.post(`${API}/documents/upload`, formData, {
        params: { tenant_id: tenantId, name: uploadForm.name, doc_type: uploadForm.doc_type },
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(lang === 'pl' ? 'Dokument za\u0142adowany' : 'Document uploaded');
      setUploadForm({ name: '', doc_type: 'general', file: null });
      loadDocuments();
    } catch (err) { toast.error('Upload error'); }
    setUploading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm(lang === 'pl' ? 'Usun\u0105\u0107?' : 'Delete?')) return;
    try {
      await axios.delete(`${API}/documents/${id}`, { headers: headers() });
      toast.success(lang === 'pl' ? 'Usuni\u0119to' : 'Deleted');
      loadDocuments();
    } catch (err) { toast.error('Error'); }
  }

  function formatSize(bytes) {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  const docTypes = ['general', 'catalog', 'technical_spec', 'certification', 'contract', 'invoice', 'other'];
  const typeLabels = { pl: { general: 'Og\u00f3lny', catalog: 'Katalog', technical_spec: 'Spec. techniczna',
    certification: 'Certyfikat', contract: 'Umowa', invoice: 'Faktura', other: 'Inny' },
    en: { general: 'General', catalog: 'Catalog', technical_spec: 'Tech Spec',
    certification: 'Certification', contract: 'Contract', invoice: 'Invoice', other: 'Other' }};

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
          {lang === 'pl' ? 'Dokumenty' : 'Documents'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {lang === 'pl' ? 'Katalogi, specyfikacje techniczne, certyfikaty' : 'Catalogs, tech specs, certifications'}
        </p>
      </div>

      {/* Upload Card */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Upload className="w-4 h-4 text-primary" />
            {lang === 'pl' ? 'Za\u0142aduj dokument' : 'Upload Document'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Nazwa' : 'Name'}</Label>
              <Input value={uploadForm.name} onChange={e => setUploadForm(p => ({...p, name: e.target.value}))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Typ' : 'Type'}</Label>
              <Select value={uploadForm.doc_type} onValueChange={v => setUploadForm(p => ({...p, doc_type: v}))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{docTypes.map(dt => <SelectItem key={dt} value={dt}>{typeLabels[lang]?.[dt] || dt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Plik' : 'File'}</Label>
              <Input type="file" onChange={e => setUploadForm(p => ({...p, file: e.target.files?.[0] || null}))} className="mt-1" />
            </div>
            <div className="flex items-end">
              <Button onClick={handleUpload} disabled={uploading} className="w-full gap-2">
                <Upload className="w-4 h-4" />
                {uploading ? '...' : (lang === 'pl' ? 'Za\u0142aduj' : 'Upload')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={!filterType ? 'default' : 'ghost'} size="sm" onClick={() => setFilterType('')}>
          {lang === 'pl' ? 'Wszystkie' : 'All'}
        </Button>
        {docTypes.map(dt => (
          <Button key={dt} variant={filterType === dt ? 'default' : 'ghost'} size="sm" onClick={() => setFilterType(dt)}>
            {typeLabels[lang]?.[dt] || dt}
          </Button>
        ))}
      </div>

      {/* Document List */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : documents.length === 0 ? (
        <Card className="border border-border"><CardContent className="text-center py-12">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{lang === 'pl' ? 'Brak dokument\u00f3w' : 'No documents'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {documents.map(doc => {
            const Icon = typeIcons[doc.doc_type] || File;
            const color = typeColors[doc.doc_type] || 'text-muted-foreground';
            return (
              <Card key={doc.id} className="border border-border hover:border-border/80 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center ${color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium truncate">{doc.name}</h4>
                      <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{typeLabels[lang]?.[doc.doc_type] || doc.doc_type}</Badge>
                        <span className="text-xs text-muted-foreground">{formatSize(doc.file_size)}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <a href={`${API}/documents/${doc.id}/download`} target="_blank" rel="noreferrer">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
